import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { FaSearch, FaTimes, FaSpinner, FaLock } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import { getCurrentUser } from '../lib/auth';

interface SearchResult {
  id: string;
  type: 'song' | 'story';
  title?: string;
  creator?: string;
  category?: string;
  language?: string;
  created_at: string;
  likes?: number;
}

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = getCurrentUser();

  // Check if user has access
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setHasAccess(false);
        return;
      }

      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      try {
        const { data: couponUsage, error } = await supabase
          .from('stripecouponusage')
          .select('*')
          .eq('user_id', user.id)
          .gte('valid_until', new Date().toISOString());

        setHasAccess(!!couponUsage && couponUsage.length > 0);
      } catch (error) {
        console.error('Error checking access:', error);
        setHasAccess(false);
      }
    };

    checkAccess();
  }, [user]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const performSearch = debounce(async (searchQuery: string) => {
    if (!searchQuery.trim() || !hasAccess) {
      setResults([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Search songs
      const { data: songs, error: songsError } = await supabase
        .from('stripesongs')
        .select('id, creator, language, song_likes, created_at')
        .or(`creator.ilike.%${searchQuery}%, song_lyrics.ilike.%${searchQuery}%`)
        .eq('is_visible', true)
        .limit(5);

      if (songsError) throw songsError;

      // Search stories
      const { data: stories, error: storiesError } = await supabase
        .from('stripestory')
        .select('id, title, category, created_at')
        .or(`title.ilike.%${searchQuery}%, content.ilike.%${searchQuery}%`)
        .limit(5);

      if (storiesError) throw storiesError;

      // Combine and format results
      const formattedResults: SearchResult[] = [
        ...(songs?.map(song => ({
          id: song.id,
          type: 'song' as const,
          creator: song.creator,
          language: song.language,
          likes: song.song_likes,
          created_at: song.created_at
        })) || []),
        ...(stories?.map(story => ({
          id: story.id,
          type: 'story' as const,
          title: story.title,
          category: story.category,
          created_at: story.created_at
        })) || [])
      ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setResults(formattedResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, 300);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(true);
    performSearch(value);
  };

  const handleResultClick = (result: SearchResult) => {
    setShowResults(false);
    setQuery('');
    if (result.type === 'song') {
      navigate(`/songs/${result.id}`);
    } else {
      navigate(`/story/${result.id}`);
    }
  };

  

   if (!hasAccess) {
    return (
      <div className="relative w-full">
        <div className="relative">
          <input
            type="text"
            value=""        // <-- Add a controlled value here
            disabled
            placeholder="Purchase access to enable search"
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-100 cursor-not-allowed"
          />
          ...
        </div>
      </div>
    );
  }
  

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowResults(true)}
          placeholder="Search songs, stories, and more..."
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? <FaSpinner className="w-4 h-4 text-gray-400 animate-spin" /> : <FaSearch className="w-4 h-4 text-gray-400" />}
        </div>
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
              setShowResults(false);
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <FaTimes className="w-4 h-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && query && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-500">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div className="py-2">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {result.type === 'song' ? result.creator : result.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {result.type === 'song' ? result.language : result.category}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 capitalize">{result.type}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;